'use client';
import { Highlight, themes } from 'prism-react-renderer';
import { HTMLProps, useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

import { IconButton } from '@/components/atoms/iconButton';

import CheckCircle from '~/svg/IconCheckmarkCircle.svg';
import IconCopy from '~/svg/IconCopy.svg';

export interface CodeBlockItemProps {
  code?: string;
  language: keyof typeof languageMap;
  showLineNumbers?: boolean;
  showCopyButton?: boolean;
  classNames?: {
    root?: string;
    iconButton?: string;
    copyIcon?: string;
    successIcon?: string;
  };
  ContainerProps?: HTMLProps<HTMLDivElement>;
}

const languageMap = {
  Python: 'python',
  'Node.js': 'jsx',
  Go: 'go',
  'C++': 'cpp',
  PHP: 'jsx',
  Java: 'Kotlin',
  'C#': 'cpp',
  cURL: 'yaml',
  JSON: 'json',
  HTML: 'markdown',
} as const;
export type CodeBlockLanguage = keyof typeof languageMap;
export const codeBlockLanguages = Object.keys(
  languageMap
) as CodeBlockLanguage[];

export function CodeBlockItem({
  code = '',
  language,
  showLineNumbers = false,
  showCopyButton = true,
  classNames,
  ContainerProps,
}: CodeBlockItemProps) {
  const [showingCopiedFeedback, setShowingCopiedFeedback] =
    useState<boolean>(false);

  const updatedTheme = {
    ...themes.palenight,
    plain: {
      ...themes.palenight.plain,
      backgroundColor: 'transparent',
    },
  };

  async function handleCopy(code: string) {
    if (showingCopiedFeedback) return;

    setShowingCopiedFeedback(true);

    await navigator.clipboard.writeText(code);
  }

  useEffect(() => {
    if (showingCopiedFeedback) {
      const timeout = setTimeout(() => {
        setShowingCopiedFeedback(false);
      }, 2000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [showingCopiedFeedback]);

  return (
    <div
      className={cn(
        'flex w-full flex-row justify-between gap-1 text-left',
        classNames?.root,
        ContainerProps?.className
      )}
      {...ContainerProps}
    >
      <Highlight
        code={code}
        theme={updatedTheme}
        language={languageMap[language]}
      >
        {({ className, style, tokens, getLineProps, getTokenProps }) => (
          <pre
            style={style}
            className={cn(className, 'word-break whitespace-pre-wrap')}
          >
            {tokens.map((line, i) => (
              <div
                key={i}
                {...getLineProps({ line })}
                className={cn(
                  'flex',
                  `min-w-[${String(tokens.length).length}em]`,
                  getLineProps({ line }).className
                )}
              >
                {showLineNumbers && (
                  <span className='text-neutral-N50/50 mr-4 inline-block text-right'>
                    {i + 1}
                  </span>
                )}
                <div>
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token })} />
                  ))}
                </div>
              </div>
            ))}
          </pre>
        )}
      </Highlight>
      {showCopyButton && (
        <div
          data-tracker-component='copy'
          className='absolute bottom-4 right-8'
        >
          <IconButton
            classNames={{
              root: cn('p-0 w-auto h-auto self-end', classNames?.root),
            }}
            noStyles
            onClick={() => handleCopy(code)}
          >
            {showingCopiedFeedback ? (
              <CheckCircle
                className={cn('text-info-I99 h-6 w-6', classNames?.successIcon)}
              />
            ) : (
              <IconCopy
                className={cn(
                  'text-neutral-N00 hover:text-neutral-N50 h-6 w-6',
                  classNames?.copyIcon
                )}
              />
            )}
          </IconButton>
        </div>
      )}
    </div>
  );
}
